// LEA-430 #2 — Refresh-/Back-Nav-Persistenz für den Prism-Funnel.
//
// Om (LEA-419-Closeout, 11.07. auch auf der One-Page-Variante bestätigt): ein
// Browser-Refresh (oder Back) am Payment-Step warf den User bis auf Schritt 1
// (Zielgruppe) zurück — mitten im echten Bezahlvorgang = wahrscheinlich
// verlorener Kauf. Der Funnel hielt ALLES nur im React-State; einzige Ausnahme
// war der einmalige, selbstlöschende ?resumeRun=-Handler (Stripe-Cancel-
// Rückkehr), der zudem nur bis zur Ergebnisseite trug.
//
// Lösung: der letzte STABILE Funnel-Stand (target/website/questions/results/
// paywall + offenes Checkout-Overlay) wird als kleines
// JSON in sessionStorage gespiegelt und beim Mount restauriert. sessionStorage
// bewusst (nicht localStorage): Tab-gebunden, stirbt mit dem Tab, leakt
// nicht zwischen Sessions. Transiente
// Phasen (analyzing/searching) werden NICHT gespeichert — ein Refresh
// mittendrin restauriert den Stand davor, statt einen halben Lauf doppelt zu
// feuern (jeder /analyze-POST erzeugt ein neues prismRun-Doc = echte Kosten).
//
// Die Rechnungsfelder des Payment-Steps (Name/Adresse/USt-IdNr.) wandern in
// einen eigenen Key: sie gehören der PrismPaymentElement-Komponente und ändern
// sich tastenweise. Kartendaten leben ausschließlich im Stripe-iframe — hier
// landet NIE etwas Zahlungsmethoden-Artiges.
//
// Alles best-effort: Private-Mode/Quota-Fehler dürfen den Funnel nie brechen.
//
// Identitäts-Bindung (Review LEA-430): beide Snapshots tragen die uid des
// eingeloggten Users und werden beim Laden verworfen, wenn sie nicht zum
// aktuellen User passt. sessionStorage überlebt einen Logout+Login im selben
// Tab (window.location.replace behält den sessionStorage-Bucket) — ohne diese
// Bindung würde User B auf einem geteilten Rechner Name/Adresse/USt-IdNr. UND
// die prismRunId von User A restauriert bekommen. clearPrismCheckoutState() im
// Logout-Handler räumt zusätzlich proaktiv auf.

const FUNNEL_KEY = 'ls_prism_funnel_v1';
const BILLING_KEY = 'ls_prism_billing_v1';

// uid des eingeloggten Users (vom Boot in window.__cortexUser gelegt). '' im
// unauth/Demo-Fall — ein '' Snapshot matcht nur einen weiteren ''-Kontext.
function currentUid(): string {
  try {
    return (
      (window as unknown as { __cortexUser?: { uid?: string } | null }).__cortexUser?.uid ?? ''
    );
  } catch {
    return '';
  }
}
// Nach 24h ist ein Snapshot stale: die Engine-Daten wären zwar serverseitig noch
// 7 Tage gültig (prismRun-TTL), aber ein Tag später mitten in Paywall/Checkout
// „weiterzumachen" wäre irreführender als ein frischer Lauf.
const SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

// Nur stabile Phasen werden persistiert (siehe Kopfkommentar).
export const STABLE_PHASES = ['target', 'website', 'questions', 'results', 'paywall'] as const;
export type PrismStablePhase = (typeof STABLE_PHASES)[number];

export interface PrismFunnelSnapshot {
  v: 1;
  savedAt: number;
  uid: string;
  phase: PrismStablePhase;
  query: string;
  website: string;
  qStep: number;
  qa: { question: string; answer: string }[];
  // AnalyzeResult/EngineResult sind lokale Typen der PrismShell — hier bewusst
  // unknown (kein Zirkular-Import); die Shell castet beim Restore zurück.
  analyze: unknown;
  engine: unknown;
  prismRunId: string | null;
  plan: { name: string; price: number } | null;
}

export interface PrismBillingSnapshot {
  v: 1;
  savedAt: number;
  uid: string;
  name: string;
  country: string;
  street: string;
  postal: string;
  city: string;
  state: string;
  vatId: string;
}

function read(key: string): unknown {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota/Private-Mode — Persistenz ist ein Nice-to-have, nie ein Blocker.
  }
}

function remove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // s. o.
  }
}

function isFresh(savedAt: unknown): savedAt is number {
  return typeof savedAt === 'number' && Date.now() - savedAt <= SNAPSHOT_TTL_MS;
}

export function saveFunnelSnapshot(snap: Omit<PrismFunnelSnapshot, 'v' | 'savedAt' | 'uid'>): void {
  write(FUNNEL_KEY, {
    v: 1,
    savedAt: Date.now(),
    uid: currentUid(),
    ...snap,
  } satisfies PrismFunnelSnapshot);
}

export function loadFunnelSnapshot(): PrismFunnelSnapshot | null {
  const raw = read(FUNNEL_KEY);
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<PrismFunnelSnapshot>;
  if (
    s.v !== 1 ||
    !isFresh(s.savedAt) ||
    s.uid !== currentUid() || // fremder/abgemeldeter User → nicht restaurieren
    !STABLE_PHASES.includes(s.phase as PrismStablePhase) ||
    typeof s.query !== 'string' ||
    typeof s.website !== 'string' ||
    typeof s.qStep !== 'number' ||
    !Array.isArray(s.qa)
  ) {
    return null;
  }
  return s as PrismFunnelSnapshot;
}

export function clearFunnelSnapshot(): void {
  remove(FUNNEL_KEY);
}

export function saveBillingSnapshot(
  snap: Omit<PrismBillingSnapshot, 'v' | 'savedAt' | 'uid'>,
): void {
  write(BILLING_KEY, {
    v: 1,
    savedAt: Date.now(),
    uid: currentUid(),
    ...snap,
  } satisfies PrismBillingSnapshot);
}

export function loadBillingSnapshot(): PrismBillingSnapshot | null {
  const raw = read(BILLING_KEY);
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<PrismBillingSnapshot>;
  if (s.v !== 1 || !isFresh(s.savedAt) || s.uid !== currentUid() || typeof s.name !== 'string') {
    return null;
  }
  return {
    v: 1,
    savedAt: s.savedAt,
    uid: s.uid,
    name: s.name,
    country: typeof s.country === 'string' ? s.country : '',
    street: typeof s.street === 'string' ? s.street : '',
    postal: typeof s.postal === 'string' ? s.postal : '',
    city: typeof s.city === 'string' ? s.city : '',
    state: typeof s.state === 'string' ? s.state : '',
    vatId: typeof s.vatId === 'string' ? s.vatId : '',
  };
}

export function clearBillingSnapshot(): void {
  remove(BILLING_KEY);
}

// Ein abgeschlossener Bezahlvorgang (Landung auf /billing/success) macht beide
// Snapshots obsolet — sonst würde ein alter Paywall-Stand über einen frisch
// bezahlten Account restauriert.
export function clearPrismCheckoutState(): void {
  clearFunnelSnapshot();
  clearBillingSnapshot();
}
